import React from "react";
import { ReactComponent as RefreshIcon } from "Assets/icon/refresh.svg";
import { ReactComponent as ShareIcon } from "Assets/icon/share.svg";
import { ReactComponent as ThumbDownIcon } from "Assets/icon/thumb-down-outline.svg";
import { ReactComponent as ThumbUpIcon } from "Assets/icon/thumb-up-outline.svg";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { OriAvatar, ORI_ORB_CROP_SCALE } from "components/ori";
import ReportStepFrame from "components/report/ReportStepFrame";
import styles from "./ChatWithOriStep.module.scss";

export interface ChatWithOriChoice {
  /** Identifies the choice to the caller. Never shown to the learner. */
  id: string;
  /** The domain's name, which is all the tile says in words. */
  label: string;
  /**
   * What the tile does, as assistive technology announces it. The shared
   * header says it once for sighted learners; a tile read on its own needs
   * the whole sentence, so it says it again.
   */
  accessibleLabel: string;
  /** The domain's glyph, supplied by the caller so no artwork is bundled. */
  icon?: React.ReactNode;
  /** The learner's score in the domain. Omitted and the tile shows none. */
  value?: number;
  /** What the score is out of. The designs show 100 throughout. */
  outOf?: number;
  /**
   * The score as assistive technology announces it, in the learner's
   * language ("55 out of 100"). The tile's name is its action, so the score
   * travels as its description; the drawn "55 / 100" is hidden from
   * assistive technology rather than read as "55 slash 100".
   */
  accessibleValue?: string;
}

export interface ChatWithOriStepProps {
  /** The step's headline, already carrying the learner's name if it should. */
  heading: string;
  /**
   * The one line over the tiles that says what picking one does, so each
   * tile can carry only its domain rather than repeating the sentence.
   */
  choicesHeading: string;
  /** The domains on offer, one tile each. The count is the caller's. */
  choices: ChatWithOriChoice[];
  /**
   * Reports which choice the learner picked. Opening Ori, and the message
   * sent to it, are the caller's job — this step raises the choice and stops.
   */
  onChoose: (choiceId: string) => void;
  /** Runs the report again from the start. Omitted and the button is absent. */
  onReplay?: () => void;
  replayLabel?: string;
  /** Leaves the report. Omitted and the button is absent. */
  onDone?: () => void;
  doneLabel?: string;
  /**
   * The frames end the report by asking whether it helped, and offering to
   * share the score. Both are drawn here; sharing is still unwired, since it
   * has no mechanism, so it renders disabled rather than looking live and
   * doing nothing. Passing a handler is what enables it.
   */
  feedbackPrompt?: string;
  onFeedback?: (helpful: boolean) => void;
  /**
   * The rating already given, shown back so the learner can see it took and
   * can change their mind. Null until they rate.
   *
   * A boolean rather than the chat's `FeedbackVote`: the handler beside it
   * already speaks in `helpful`, and the report has no third state to carry.
   */
  feedbackHelpful?: boolean | null;
  feedbackHelpfulLabel?: string;
  feedbackUnhelpfulLabel?: string;
  onShare?: () => void;
  shareLabel?: string;
}

/**
 * The report's last step: the learner commits to improving one domain, and
 * the choice is handed back so the flow can carry them into Ori.
 */
const ChatWithOriStep: React.FC<ChatWithOriStepProps> = ({
  heading,
  choicesHeading,
  choices,
  onChoose,
  onReplay,
  replayLabel,
  onDone,
  doneLabel,
  feedbackPrompt,
  onFeedback,
  feedbackHelpful = null,
  feedbackHelpfulLabel,
  feedbackUnhelpfulLabel,
  onShare,
  shareLabel,
}) => (
  <ReportStepFrame
    footer={
      feedbackPrompt && (
        <div className={styles.feedback} data-testid="chat-with-ori-feedback">
          <p className={styles["feedback-prompt"]}>{feedbackPrompt}</p>
          <div className={styles["feedback-buttons"]}>
            <button
              type="button"
              className={styles["feedback-button"]}
              onClick={() => onFeedback?.(false)}
              disabled={!onFeedback}
              aria-pressed={feedbackHelpful === false}
              data-rated={feedbackHelpful !== null}
              aria-label={feedbackUnhelpfulLabel}
              data-testid="chat-with-ori-feedback-down"
            >
              <ThumbDownIcon
                className={styles["feedback-icon"]}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className={styles["feedback-button"]}
              onClick={() => onFeedback?.(true)}
              disabled={!onFeedback}
              aria-pressed={feedbackHelpful === true}
              data-rated={feedbackHelpful !== null}
              aria-label={feedbackHelpfulLabel}
              data-testid="chat-with-ori-feedback-up"
            >
              <ThumbUpIcon
                className={styles["feedback-icon"]}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      )
    }
    actions={
      (onReplay || onDone || shareLabel) && (
        <div className={styles.actions} data-testid="chat-with-ori-actions">
          {onReplay && (
            <button
              type="button"
              className={styles["action-outlined"]}
              onClick={onReplay}
              data-testid="chat-with-ori-replay"
            >
              <RefreshIcon
                className={styles["action-icon"]}
                aria-hidden="true"
              />
              {replayLabel}
            </button>
          )}
          {shareLabel && (
            <button
              type="button"
              className={styles["action-outlined"]}
              onClick={onShare}
              disabled={!onShare}
              data-testid="chat-with-ori-share"
            >
              <ShareIcon className={styles["action-icon"]} aria-hidden="true" />
              {shareLabel}
            </button>
          )}
          {onDone && (
            <button
              type="button"
              className={styles["action-filled"]}
              onClick={onDone}
              data-testid="chat-with-ori-done"
            >
              {doneLabel}
              <ChevronIcon
                className={styles["action-icon"]}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )
    }
  >
    <div className={styles.step} data-testid="chat-with-ori-step">
      <div className={styles.intro}>
        <OriAvatar
          size={36}
          cropScale={ORI_ORB_CROP_SCALE}
          testId="chat-with-ori-mark"
        />
        <h1 className={styles.heading}>{heading}</h1>
      </div>

      {choices.length > 0 && (
        <p className={styles["choices-heading"]}>{choicesHeading}</p>
      )}
      <ul className={styles.choices} data-testid="chat-with-ori-choices">
        {choices.map((choice) => (
          <li key={choice.id} className={styles.choice}>
            <button
              type="button"
              className={styles.tile}
              onClick={() => onChoose(choice.id)}
              aria-label={choice.accessibleLabel}
              aria-describedby={
                choice.value !== undefined && choice.accessibleValue
                  ? `chat-with-ori-choice-${choice.id}-score`
                  : undefined
              }
              data-testid={`chat-with-ori-choice-${choice.id}`}
            >
              <span className={styles["tile-top"]} aria-hidden="true">
                {choice.icon && (
                  <span className={styles["tile-icon"]}>{choice.icon}</span>
                )}
                <ChevronIcon className={styles["tile-chevron"]} />
              </span>
              <span className={styles["tile-label"]}>{choice.label}</span>
              {choice.value !== undefined && (
                <span className={styles["tile-value"]} aria-hidden="true">
                  <span className={styles["tile-number"]}>{choice.value}</span>
                  <span className={styles["tile-out-of"]}>
                    / {choice.outOf ?? 100}
                  </span>
                </span>
              )}
              {choice.value !== undefined && choice.accessibleValue && (
                <span
                  id={`chat-with-ori-choice-${choice.id}-score`}
                  className={styles["visually-hidden"]}
                >
                  {choice.accessibleValue}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  </ReportStepFrame>
);

export default ChatWithOriStep;
